import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";

const images = require.context("../../public/img/portfolio", true);
const imageList = images.keys().map((image) => {
  const importedImage = images(image);
  return importedImage.default ? importedImage.default : importedImage;
});

function shuffle(array) {
  const a = [...array];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

const AUTOPLAY_MS = 4000;
const SWIPE_THRESHOLD_PX = 50;

export const Gallery = () => {
  const scrambledImageList = useMemo(() => shuffle(imageList), []);
  const slideCount = scrambledImageList.length;

  const [currentIndex, setCurrentIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const [landscapeSlides, setLandscapeSlides] = useState({});

  const touchStartXRef = useRef(null);

  const handleImageLoad = useCallback((index, event) => {
    const { naturalWidth, naturalHeight } = event.target;
    if (naturalWidth > naturalHeight) {
      setLandscapeSlides((prev) => (prev[index] ? prev : { ...prev, [index]: true }));
    }
  }, []);

  const goToIndex = useCallback(
    (index) => {
      if (!slideCount) return;
      setCurrentIndex(((index % slideCount) + slideCount) % slideCount);
    },
    [slideCount]
  );

  const goToPrev = useCallback(() => goToIndex(currentIndex - 1), [currentIndex, goToIndex]);
  const goToNext = useCallback(() => goToIndex(currentIndex + 1), [currentIndex, goToIndex]);

  // Autoplay, restarts (and thus resets its countdown) after any manual navigation.
  useEffect(() => {
    if (isPaused || slideCount < 2) return;
    const id = window.setTimeout(() => {
      setCurrentIndex((prev) => (prev + 1) % slideCount);
    }, AUTOPLAY_MS);
    return () => window.clearTimeout(id);
  }, [currentIndex, isPaused, slideCount]);

  const handleKeyDown = (event) => {
    if (event.key === "ArrowLeft") {
      event.preventDefault();
      goToPrev();
    } else if (event.key === "ArrowRight") {
      event.preventDefault();
      goToNext();
    }
  };

  const handleTouchStart = (event) => {
    touchStartXRef.current = event.touches[0].clientX;
    setIsPaused(true);
  };

  const handleTouchEnd = (event) => {
    if (touchStartXRef.current === null) return;
    const deltaX = event.changedTouches[0].clientX - touchStartXRef.current;
    touchStartXRef.current = null;
    setIsPaused(false);

    if (deltaX > SWIPE_THRESHOLD_PX) {
      goToPrev();
    } else if (deltaX < -SWIPE_THRESHOLD_PX) {
      goToNext();
    }
  };

  if (!slideCount) return null;

  return (
    <div id="gallery" className="image-gallery">
      <div
        className="carousel"
        role="region"
        aria-roledescription="carousel"
        aria-label="Photo gallery"
        tabIndex={0}
        onKeyDown={handleKeyDown}
        onMouseEnter={() => setIsPaused(true)}
        onMouseLeave={() => setIsPaused(false)}
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
      >
        <div className="image-container">
          <div
            className="carousel-track"
            style={{ transform: `translateX(-${currentIndex * 100}%)` }}
          >
            {scrambledImageList.map((src, index) => (
              <div
                className="carousel-slide"
                key={src}
                aria-hidden={index !== currentIndex}
              >
                <img
                  src={src}
                  alt={`Slide ${index + 1} of ${slideCount}`}
                  className={`image ${landscapeSlides[index] ? "image-contain" : ""}`}
                  draggable={false}
                  onLoad={(event) => handleImageLoad(index, event)}
                />
              </div>
            ))}
          </div>

          <button
            type="button"
            className="carousel-btn prev"
            aria-label="Previous slide"
            onClick={goToPrev}
          >
            <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true">
              <path
                d="M15 4 7 12l8 8"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
          <button
            type="button"
            className="carousel-btn next"
            aria-label="Next slide"
            onClick={goToNext}
          >
            <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true">
              <path
                d="M9 4l8 8-8 8"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
        </div>

        <div className="carousel-dots">
          {scrambledImageList.map((src, index) => (
            <button
              type="button"
              key={src}
              className={`carousel-dot ${index === currentIndex ? "active" : ""}`}
              aria-label={`Go to slide ${index + 1}`}
              aria-current={index === currentIndex}
              onClick={() => goToIndex(index)}
            />
          ))}
        </div>
      </div>
    </div>
  );
};
